// Session gate, backed by TanStack Query for a DETERMINISTIC loading state.
//
// better-auth's reactive useSession can momentarily report "no session"
// before its first fetch resolves on a fresh page load — which made
// RequireAuth bounce authenticated users (with a perfectly valid cookie) to
// /login on refresh or deep-link. A query gives a real `isPending` that's
// true until the first response lands, so RequireAuth shows a loading state
// instead of redirecting prematurely. signOut does a hard navigation
// (UserMenu), so this cache resets on logout without manual invalidation.
//
// State machine : 'loading' -> 'authenticated' | 'anonymous' | 'error'.

import { useQuery } from '@tanstack/react-query';

export function useSession() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      // Plain fetch with explicit credentials — the gate must not depend on
      // the better-auth client's transport sending cookies. get-session
      // returns 200 with the session payload, or 200 with `null` when
      // anonymous.
      const res = await fetch('/api/auth/get-session', {
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`session_http_${res.status}`);
      return (await res.json()) ?? null; // { user, session } | null
    },
    staleTime: 60_000,
    retry: false,
  });

  if (isPending) return { status: 'loading', user: null, session: null };
  if (isError) return { status: 'error', user: null, session: null, error: String(error) };
  if (data?.user) return { status: 'authenticated', user: data.user, session: data.session };
  return { status: 'anonymous', user: null, session: null };
}
